import { Box } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { SyntheticEvent, useCallback, useMemo, useReducer, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { DealsService } from 'service/deals.service';
import { NewDealForm } from '../NewDealForm/NewDealForm';
import { ContentProductionDealDetails } from './components/ContentProductionDealDetails';
import { DealActivityHistory } from './components/DealActivityHistory';
import { DealDetailsContent } from './components/DealDetailsContent';
import { DealDetailsHeader } from './components/DealDetailsHeader';
import { PriceBookProducts } from './components/PriceBookProducts';
import { formatStageDisplay, mapDealToDetailFields } from './DealsDetails.utils';
import { useDealDetailsData } from './hooks/useDealDetailsData';
import { useDealFieldUpdates } from './hooks/useDealFieldUpdates';

export const DealsDetails = ({
  isOpen,
  deal,
  onClose,
  handleEdit,
  onDealDeleted,
  getDeals,
}: {
  isOpen: boolean;
  deal?: any;
  onClose: () => void;
  handleEdit?: () => void;
  onDealDeleted?: () => void;
  getDeals?: () => void;
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    rename: false,
    delete: false,
    activityHistory: false,
  });

  const { dealData, setDealData, loading, ownerOptions, stageOptions, normalizedDealType } =
    useDealDetailsData(isOpen, deal);

  const { alertState, getAlertContent, setAlertState, startOperation, completeOperation } =
    useAlertStatus();

  const { handleDealStageNameChange, handleFieldChange } = useDealFieldUpdates(
    dealData,
    setDealData,
    ownerOptions,
    getDeals,
  );

  const fields = useMemo(() => {
    return dealData
      ? mapDealToDetailFields(dealData, normalizedDealType || 'Commercial/Endorsement')
      : {
          opportunityInfo: [],
          financialInfo: [],
          attributionTracking: [],
          additionalInfo: [],
          typeSpecificFields: [],
        };
  }, [dealData, normalizedDealType]);

  const handleDeleteClick = useCallback(() => {
    modalDispatch({ type: 'OPEN', payload: 'delete' });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!dealData?.id && !dealData?.dealId) return;
    const dealId = dealData.id || dealData.dealId;
    try {
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      await DealsService.deleteDeal(dealId);
      if (onDealDeleted) {
        onDealDeleted();
      }
      onClose();
    } catch (error) {
      console.error('Failed to delete deal:', error);
    }
  }, [dealData, onDealDeleted, onClose]);

  const handleChange = useCallback((_: SyntheticEvent, newValue: number) => {
    sessionStorage.setItem('currentAdminConsoleTab', newValue.toString());
    setTabValue(newValue);
  }, []);

  const dealStageName = dealData?.dealStageName || dealData?.stage || '';
  const currentStageDisplay = formatStageDisplay(dealStageName);

  const handleEditClick = useCallback(() => {
    setOpenEditForm(true);
    onClose();
    if (handleEdit) handleEdit();
  }, [handleEdit, onClose]);

  const handleActivityHistoryClick = useCallback(() => {
    modalDispatch({ type: 'OPEN', payload: 'activityHistory' });
  }, []);

  const tabs = useMemo(() => {
    const isContentProduction = normalizedDealType === 'Content/Production';

    return [
      {
        id: 1,
        label: 'Deal Details',
        isHidden: tabValue !== 0,
        Component: isContentProduction ? (
          <ContentProductionDealDetails
            dealData={dealData}
            loading={loading}
            onFieldChange={handleFieldChange}
            ownerOptions={ownerOptions}
            stageOptions={stageOptions}
            onActivityHistoryClick={handleActivityHistoryClick}
            completeOperation={completeOperation}
            startOperation={startOperation}
          />
        ) : (
          <DealDetailsContent
            fields={fields}
            dealData={dealData}
            loading={loading}
            onFieldChange={handleFieldChange}
            ownerOptions={ownerOptions}
            stageOptions={stageOptions}
            onActivityHistoryClick={handleActivityHistoryClick}
            completeOperation={completeOperation}
            startOperation={startOperation}
          />
        ),
      },
      {
        id: 2,
        label: 'Price Book & Products',
        isHidden: tabValue !== 1,
        Component: <PriceBookProducts dealData={dealData} loading={loading} />,
      },
    ];
  }, [
    tabValue,
    fields,
    dealData,
    loading,
    handleFieldChange,
    ownerOptions,
    stageOptions,
    normalizedDealType,
  ]);

  if (!dealData) return null;

  return (
    <>
      <CustomDrawer state={isOpen} onClose={onClose}>
        <Box
          sx={{
            width: { xs: '427px', xl: '527px' },
            height: '100vh',
            display: 'flex',
            padding: '32px 24px 16px',
            flexDirection: 'column',
            bgcolor: 'white',
            overflow: 'scroll',
          }}
        >
          <DealDetailsHeader
            dealData={dealData}
            normalizedDealType={normalizedDealType}
            currentStageDisplay={currentStageDisplay}
            onStageChange={handleDealStageNameChange}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onClose={onClose}
          />

          <Box
            sx={{
              flex: 1,
              overflow: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <CustomTab tabs={tabs} handleChange={handleChange} value={tabValue} />
          </Box>
        </Box>
      </CustomDrawer>
      {openEditForm && dealData && (dealData.id || dealData.dealId) && (
        <NewDealForm
          isEdit={true}
          dealId={dealData.id || dealData.dealId}
          deal={dealData}
          onDealCreated={() => {
            setOpenEditForm(false);
          }}
        />
      )}
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleDeleteConfirm}
        title='Delete Deal'
        description={`You're about to delete the deal "${dealData?.name || dealData?.title || 'this deal'}". This action cannot be undone. Are you sure you want to delete?`}
      />
      {dealData && (dealData.id || dealData.dealId) && (
        <DealActivityHistory
          isModalOpen={modalState.activityHistory || false}
          modalDispatch={modalDispatch}
          dealId={dealData.id || dealData.dealId}
        />
      )}
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </>
  );
};
